import { useState } from "react";
import { useNotify, useRefresh, useUpdate } from "ra-core";
import {
  Calendar,
  Check,
  Clock,
  Copy,
  ExternalLink,
  Layers,
  Mail,
  MessageSquare,
  Play,
  RotateCcw,
  Sparkles,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getSupabaseClient } from "../providers/supabase/supabase";
import type { Contact, SequenceStep, GroupEntityId } from "../types";
import { GROUP_ENTITIES, GROUP_ENTITY_CHOICES } from "../types";
import { getGroupEntity } from "../misc/GroupEntitySelector";

export function generateDefaultSequence(
  contact: Contact,
  entityId: GroupEntityId = (contact.group_entity as GroupEntityId) || "five_lens",
): SequenceStep[] {
  const firstName = contact.first_name || "there";
  const company =
    contact.company_name || contact.detected_company || "your team";
  const title = contact.title || "Leader";
  const entity = getGroupEntity(entityId);

  let step1Message = "";
  let step2Message = "";
  let step3Message = "";

  if (entity.id === "five_lens") {
    step1Message =
      contact.ai_icebreaker ||
      `Hey ${firstName} — noticed your leadership as ${title} at ${company}. Impressed by how you foster team performance and culture — would love to connect!`;
    step2Message = `Hi ${firstName},

Following up on my note earlier. At Five Lens Enneagram (fivelensenneagram.com), we partner with progressive leaders to unlock deep emotional agility and team performance using our 5-lens psychological diagnostics.

We've helped executive teams across fast-growing organizations accelerate trust and resolve behavioral friction rapidly. Would you be open to a 10-minute chat this week to swap notes on what's working best for ${company}?`;
    step3Message = `Hey ${firstName} — figured you're heads-down leading at ${company}. No worries at all if timing is tight!

I'll leave our diagnostic framework overview here (fivelensenneagram.com). Should we touch base next month, or is someone else on your executive team leading leadership profiling & team dynamics?`;
  } else if (entity.id === "learn_smartly") {
    step1Message =
      contact.ai_icebreaker ||
      `Hey ${firstName} — came across your work leading initiatives at ${company}. Impressed by how you scale capability and talent — would love to connect!`;
    step2Message = `Hi ${firstName},

Following up on my message earlier. At LearnSmartly Institute (learnsmartly.institute), we design high-impact corporate learning academies that bridge capability gaps and transform workforce execution.

Given your growth milestones at ${company}, would you be open to a brief 10-minute intro to explore how modern academies are accelerating ramp times for teams like yours?`;
    step3Message = `Hey ${firstName} — completely understand you're busy driving execution at ${company}.

Leaving our capability framework here (learnsmartly.institute). Should we reconnect down the road, or is someone else on the L&D / people team spearheading this right now?`;
  } else {
    // ennea_world
    step1Message =
      contact.ai_icebreaker ||
      `Hey ${firstName} — came across your profile leading initiatives at ${company}. Would love to connect and exchange perspectives on executive team alignment!`;
    step2Message = `Hi ${firstName},

Following up on my note earlier. At Ennea World (ennea.world), we empower global executive teams and founders with structured alignment frameworks to eliminate organizational friction and scale internationally.

Would you be open to a quick 10-minute conversation this week to see if our executive ecosystem can support your strategic objectives at ${company}?`;
    step3Message = `Hey ${firstName} — no problem at all if timing isn't ideal right now with everything on your plate at ${company}.

I'll leave our executive overview here (ennea.world). Feel free to ping me whenever it's top of mind!`;
  }

  return [
    {
      step: 1,
      channel: "linkedin",
      day: 1,
      title: "Step 1: LinkedIn Warm Opener",
      message: step1Message,
      status: "pending",
    },
    {
      step: 2,
      channel: "email",
      day: 3,
      title: "Step 2: Value-Add Email Follow-up",
      message: step2Message,
      status: "pending",
    },
    {
      step: 3,
      channel: "linkedin",
      day: 7,
      title: "Step 3: Executive Closing Touchpoint",
      message: step3Message,
      status: "pending",
    },
  ];
}

export function AiSequenceBadge({
  contact,
  onClick,
}: {
  contact: Contact;
  onClick?: () => void;
}) {
  const sequence = contact.ai_sequence;
  const isEnrolled = Array.isArray(sequence) && sequence.length > 0;
  const completedCount = isEnrolled
    ? sequence.filter((s) => s.status === "completed").length
    : 0;

  if (!isEnrolled) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="inline-flex items-center gap-1 rounded-full border border-dashed border-indigo-400/40 bg-indigo-500/5 px-2 py-0.5 text-[11px] font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-500/10 cursor-pointer transition-colors"
      >
        <Sparkles className="size-3" />
        <span>+ AI Cadence</span>
      </button>
    );
  }

  return (
    <Badge
      variant="outline"
      onClick={onClick}
      className="inline-flex items-center gap-1 border-indigo-500/30 bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 font-medium text-[11px] cursor-pointer hover:bg-indigo-500/20 transition-colors"
    >
      <Layers className="size-3" />
      <span>
        Cadence ({completedCount}/{sequence.length})
      </span>
    </Badge>
  );
}

export function AiSequenceModal({
  contact,
  open,
  onOpenChange,
}: {
  contact: Contact;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const notify = useNotify();
  const refresh = useRefresh();
  const [update] = useUpdate<Contact>();
  const [selectedEntity, setSelectedEntity] = useState<GroupEntityId>(
    (contact.group_entity as GroupEntityId) || "five_lens",
  );
  const [steps, setSteps] = useState<SequenceStep[]>(() => {
    return Array.isArray(contact.ai_sequence) && contact.ai_sequence.length > 0
      ? contact.ai_sequence
      : generateDefaultSequence(contact, (contact.group_entity as GroupEntityId) || "five_lens");
  });

  const handleEntityChange = async (newEntity: GroupEntityId) => {
    setSelectedEntity(newEntity);
    setSteps(generateDefaultSequence(contact, newEntity));
    // Persist preferred entity to contact
    try {
      const supabase = getSupabaseClient();
      await supabase.from("contacts").update({ group_entity: newEntity }).eq("id", contact.id);
    } catch {
      // non-blocking
    }
  };
  const [activeTab, setActiveTab] = useState<number>(0);
  const [copiedStep, setCopiedStep] = useState<number | null>(null);
  const [isScheduling, setIsScheduling] = useState(false);

  const currentStep = steps[activeTab] || steps[0];
  const workEmail =
    contact.email_jsonb?.find((e) => e.type === "Work")?.email ||
    contact.email_jsonb?.[0]?.email;

  const handleCopyMessage = async (stepIdx: number, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedStep(stepIdx);
      notify("Outreach copy copied to clipboard", { type: "info" });
      setTimeout(() => setCopiedStep(null), 2000);
    } catch {
      notify("Failed to copy copy", { type: "error" });
    }
  };

  const handleUpdateStepMessage = (text: string) => {
    setSteps((prev) =>
      prev.map((s, idx) => (idx === activeTab ? { ...s, message: text } : s)),
    );
  };

  const handleToggleStepCompleted = (stepIdx: number) => {
    setSteps((prev) =>
      prev.map((s, idx) =>
        idx === stepIdx
          ? {
              ...s,
              status: s.status === "completed" ? "pending" : "completed",
            }
          : s,
      ),
    );
  };

  const handleEnrollAndSchedule = async () => {
    setIsScheduling(true);
    try {
      const supabase = getSupabaseClient();
      const now = new Date();

      // Create/Schedule CRM Tasks for pending steps
      const updatedSteps = [...steps];
      for (let i = 0; i < updatedSteps.length; i++) {
        const step = updatedSteps[i];
        if (step.status === "pending" && !step.task_id) {
          const dueDate = new Date(now);
          dueDate.setDate(dueDate.getDate() + (step.day - 1));

          const taskPayload = {
            contact_id: contact.id,
            type: step.channel === "linkedin" ? "linkedin-dm" : "email",
            text: `${step.title}: "${step.message.slice(0, 120)}..."`,
            due_date: dueDate.toISOString(),
            sales_id: contact.sales_id || 1,
          };

          const { data: createdTask } = await supabase
            .from("tasks")
            .insert(taskPayload)
            .select("id")
            .single();

          if (createdTask?.id) {
            updatedSteps[i] = { ...step, task_id: createdTask.id };
          }
        }
      }

      // Compute next follow-up date
      const nextPending = updatedSteps.find((s) => s.status === "pending");
      const nextFollowupDate = nextPending
        ? new Date(
            now.getTime() + (nextPending.day - 1) * 24 * 60 * 60 * 1000,
          ).toISOString()
        : null;

      await update(
        "contacts",
        {
          id: contact.id,
          data: {
            ai_sequence: updatedSteps,
            outreach_stage:
              contact.outreach_stage === "new"
                ? "hook_ready"
                : contact.outreach_stage,
            next_followup_at: nextFollowupDate,
          },
          previousData: contact,
        },
        { mutationMode: "optimistic" },
      );

      notify(
        `Enrolled ${contact.first_name || "contact"} into 3-Step AI Sequence! Tasks scheduled in CRM.`,
        { type: "info" },
      );
      refresh();
      onOpenChange(false);
    } catch (err) {
      notify(
        `Failed to schedule sequence: ${err instanceof Error ? err.message : "Unknown error"}`,
        { type: "error" },
      );
    } finally {
      setIsScheduling(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="flex size-7 items-center justify-center rounded-lg bg-indigo-500/15 text-indigo-600 dark:text-indigo-400">
              <Sparkles className="size-4" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold">
                AI Multi-Channel Outreach Sequence
              </DialogTitle>
              <DialogDescription className="text-xs">
                3-touch tailored cadence for {contact.first_name}{" "}
                {contact.last_name} ({contact.title || "Lead"} at{" "}
                {contact.company_name || contact.detected_company || "Company"})
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Brand / Company Entity Selector */}
        <div className="flex flex-wrap items-center justify-between gap-2 p-2.5 rounded-lg border bg-muted/40">
          <div className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
            <span>Outreach Brand:</span>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {GROUP_ENTITY_CHOICES.map((choice) => {
              const isSelected = selectedEntity === choice.id;
              const entity = GROUP_ENTITIES[choice.id as GroupEntityId];
              return (
                <button
                  key={choice.id}
                  type="button"
                  onClick={() => handleEntityChange(choice.id as GroupEntityId)}
                  className={`px-2.5 py-1 text-xs rounded-md font-medium transition-all cursor-pointer ${
                    isSelected
                      ? "bg-primary text-primary-foreground shadow-xs font-semibold"
                      : "bg-background border text-muted-foreground hover:text-foreground"
                  }`}
                  title={`${entity.name} (${entity.domain}) — ${entity.tagline}`}
                >
                  {choice.name.split(" ")[0]} {entity.badge}
                </button>
              );
            })}
          </div>
        </div>

        {/* Cadence Step Tabs */}
        <div className="grid grid-cols-3 gap-2 pt-2">
          {steps.map((s, idx) => {
            const isActive = activeTab === idx;
            const isCompleted = s.status === "completed";
            return (
              <button
                key={s.step}
                type="button"
                onClick={() => setActiveTab(idx)}
                className={`flex flex-col gap-1 p-2.5 rounded-lg border text-left transition-all cursor-pointer ${
                  isActive
                    ? "border-indigo-500 bg-indigo-500/10 shadow-xs"
                    : "border-border/70 bg-card hover:bg-muted/50"
                }`}
              >
                <div className="flex items-center justify-between text-xs font-semibold">
                  <span className="flex items-center gap-1.5 text-foreground">
                    {s.channel === "linkedin" ? (
                      <MessageSquare className="size-3 text-sky-500" />
                    ) : (
                      <Mail className="size-3 text-amber-500" />
                    )}
                    Touch #{s.step}
                  </span>
                  <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                    <Clock className="size-2.5" />
                    Day {s.day}
                  </span>
                </div>
                <div className="text-[11px] text-muted-foreground truncate">
                  {s.title}
                </div>
                {isCompleted && (
                  <span className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">
                    <Check className="size-3" /> Sent / Completed
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Active Step Editor & Quick Action */}
        <div className="mt-3 rounded-xl border bg-muted/20 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-foreground">
                {currentStep.title}
              </span>
              <Badge variant="outline" className="text-[10px] font-medium">
                Day {currentStep.day} ·{" "}
                {currentStep.channel === "linkedin" ? "LinkedIn" : "Email"}
              </Badge>
            </div>

            <div className="flex items-center gap-1.5">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() =>
                  handleCopyMessage(activeTab, currentStep.message)
                }
                className="h-7 text-xs gap-1 cursor-pointer"
              >
                {copiedStep === activeTab ? (
                  <>
                    <Check className="size-3 text-emerald-500" />
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="size-3" />
                    <span>Copy Text</span>
                  </>
                )}
              </Button>

              {currentStep.channel === "linkedin" && contact.linkedin_url && (
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    navigator.clipboard.writeText(currentStep.message);
                    window.open(
                      contact.linkedin_url || "",
                      "_blank",
                      "noopener,noreferrer",
                    );
                  }}
                  className="h-7 text-xs gap-1 cursor-pointer"
                >
                  <ExternalLink className="size-3" />
                  <span>Open LinkedIn</span>
                </Button>
              )}

              {currentStep.channel === "email" && workEmail && (
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    const mailto = `mailto:${workEmail}?subject=${encodeURIComponent(
                      `Quick question regarding ${contact.company_name || contact.detected_company || "growth"}`,
                    )}&body=${encodeURIComponent(currentStep.message)}`;
                    window.open(mailto);
                  }}
                  className="h-7 text-xs gap-1 cursor-pointer"
                >
                  <Mail className="size-3" />
                  <span>Open Email</span>
                </Button>
              )}
            </div>
          </div>

          <textarea
            value={currentStep.message}
            onChange={(e) => handleUpdateStepMessage(e.target.value)}
            rows={5}
            className="w-full text-xs font-sans leading-relaxed rounded-lg border bg-background p-3 focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-none"
            placeholder="Edit sequence message..."
          />

          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={currentStep.status === "completed"}
                onChange={() => handleToggleStepCompleted(activeTab)}
                className="rounded border-border text-indigo-600 focus:ring-indigo-500"
              />
              <span>Mark Touch #{currentStep.step} as sent / completed</span>
            </label>

            <button
              type="button"
              onClick={() => {
                const fresh = generateDefaultSequence(contact);
                setSteps(fresh);
                notify("Reset sequence copy to AI default", { type: "info" });
              }}
              className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground cursor-pointer"
            >
              <RotateCcw className="size-3" />
              <span>Reset Copy</span>
            </button>
          </div>
        </div>

        <DialogFooter className="mt-2 flex items-center justify-between sm:justify-between w-full">
          <div className="text-xs text-muted-foreground flex items-center gap-1.5">
            <Calendar className="size-3.5 text-indigo-500" />
            <span>Schedules 3 actionable follow-up tasks on your CRM board</span>
          </div>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="default"
              disabled={isScheduling}
              onClick={handleEnrollAndSchedule}
              className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white cursor-pointer"
            >
              <Play className="size-3.5 fill-current" />
              <span>
                {isScheduling ? "Scheduling..." : "Enroll & Schedule Cadence"}
              </span>
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
